import { type ReactNode, useState } from 'react';
import { Button } from './button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog.js';
import { Spinner } from './spinner.js';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, the confirm button gets the danger variant. */
  destructive?: boolean;
  /** When true, the confirm button shows a spinner and is disabled. */
  pending?: boolean;
  onConfirm: () => unknown;
}

/**
 * Replacement for `window.confirm()` for destructive actions.
 *
 * Renders the standard "title / description / cancel / confirm" pattern using
 * the existing `Dialog` primitive. Keep the description short and explicit
 * about consequences ("Cancelar a assinatura? Você mantém acesso até X.").
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  pending = false,
  onConfirm,
}: ConfirmDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
            type="button"
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={() => {
              void onConfirm();
            }}
            disabled={pending}
            type="button"
          >
            {pending ? <Spinner size="sm" className="text-current" /> : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Convenience hook for ad-hoc confirmation flows. Returns `{open, confirm, dialogProps}`
 * — caller spreads `dialogProps` into <ConfirmDialog> and calls `confirm(handler)` to
 * trigger the prompt. The handler runs on confirm.
 */
export const useConfirm = () => {
  const [open, setOpen] = useState(false);
  const [handler, setHandler] = useState<(() => unknown) | null>(null);

  const confirm = (onConfirm: () => unknown) => {
    setHandler(() => onConfirm);
    setOpen(true);
  };

  return {
    open,
    confirm,
    dialogProps: {
      open,
      onOpenChange: setOpen,
      onConfirm: async () => {
        if (handler) await handler();
        setOpen(false);
        setHandler(null);
      },
    },
  };
};
